import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { LayerTypeDTO } from 'business/models/Models';
import { WellNavigationDetails } from 'components/Experiment/constants';
import { ESidePanelTabs } from 'components/main/constants';
import { ExpDashboardContext } from 'components/main/ExperimentDashboardContext';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { Rect } from 'react-konva';
import Konva from 'konva';
import { usePreserveExperiment, useRMDispatch } from 'redux/hooks';
import {
  appendLayer,
  setIsLayerSelected,
  setSelectedLayerId,
  setShouldShowLayerEditPanel,
  setShouldShowLayerIngredientPanel,
} from 'redux/reducers/experimentDataReducer';
import { zoomToFocusArea, platePosition } from 'components/canvas/utils';
import { GetDefaultPlatePadding } from 'utils/Global';
import { useAllPlates, useAllWellNavigationDetails } from 'redux/hooks/experiment';
import { useLastSelectedWellRect } from 'redux/hooks/well';

export const useGetWellSelectionContent = (
  exp: ExperimentDtoClient,
  allWellNavigationDetails: WellNavigationDetails[],
  selectedWellColor: string | number | boolean,
  strokeNormalizer: number,
) => {
  return useMemo(() => {
    if (!exp?.plateDto || !allWellNavigationDetails) {
      return null;
    }
    return exp.plateDto
      .flatMap((plate, plateIndex) => {
        if (!plate?.wellRects) {
          return [];
        }
        return allWellNavigationDetails
          .filter((w) => w.plateNum === plateIndex + 1)
          .map((w, index) => {
            const rect = plate.wellRects[w.wellNum - 1];
            if (!rect) {
              return null;
            }
            return (
              <Rect
                name="wellSelectionRect"
                key={`wellSelectionRect-${index}`}
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                stroke={selectedWellColor ? (selectedWellColor as string) : '#F7A246'}
                strokeWidth={3 / strokeNormalizer}
                shadowBlur={0}
                listening={false}
              />
            );
          });
      })
      .filter(Boolean);
  }, [exp?.plateDto, allWellNavigationDetails, selectedWellColor]);
};

export const useUpdateLayerStates = () => {
  const callPreserveUndoableState = usePreserveExperiment();
  const dispatch = useRMDispatch();
  const expDashboardContext = useContext(ExpDashboardContext);

  return (newLayer, isShowLayerEditPanel = false) => {
    callPreserveUndoableState();
    dispatch(appendLayer(newLayer));
    dispatch(setSelectedLayerId(newLayer.id));
    dispatch(setIsLayerSelected(true));
    expDashboardContext.setSelectedSidePanelTab(ESidePanelTabs.Layers);
    dispatch(setShouldShowLayerIngredientPanel(true));
    if (
      newLayer.type === LayerTypeDTO.Protein ||
      newLayer.type === LayerTypeDTO.Fragment ||
      newLayer.type === LayerTypeDTO.Seed
    ) {
      dispatch(setShouldShowLayerEditPanel(isShowLayerEditPanel));
    }
  };
};

export const useZoomToPlateHandler = (
  stageRef: React.MutableRefObject<Konva.Stage | undefined>,
) => {
  const plates = useAllPlates();
  return useCallback(
    (plateID: number) => {
      if (!plates?.length || !stageRef?.current) {
        return;
      }
      const selectedPlate = plates.find((plate) => plate.plateId === plateID);
      if (!selectedPlate) {
        return;
      }
      const wellHeight =
        selectedPlate?.wellRects?.[0]?.h ?? plates?.[0]?.wellRects?.[0]?.h;
      const padding = wellHeight ? wellHeight / 2 : GetDefaultPlatePadding();
      const plateBarcodeHeight = wellHeight ? wellHeight / 2 : padding;
      const { startX: x, startY: y, width, height } = selectedPlate;
      zoomToFocusArea(stageRef?.current, {
        x: x - padding,
        y: y - padding,
        width: width + padding,
        height: height + padding + plateBarcodeHeight,
      });
    },
    [plates, stageRef],
  );
};

type UseInitialPlateZoomParams = {
  plateId: number | null;
  plateCount: number | null;
  selectedPlate?: { width?: number; height?: number } | null;
  isScreenRoute: boolean;
  experimentLoadProgress: number;
  selectedSidePanelTab: unknown;
  canvasStageRef: React.MutableRefObject<Konva.Stage | null>;
  canvasContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  zoomToPlateHandler: (plateId: number) => void;
};

export const useInitialPlateZoom = ({
  plateId,
  plateCount,
  selectedPlate,
  isScreenRoute,
  experimentLoadProgress,
  selectedSidePanelTab,
  canvasStageRef,
  canvasContainerRef,
  zoomToPlateHandler,
}: UseInitialPlateZoomParams) => {
  const initialPlateZoomRef = useRef(false);
  useLayoutEffect(() => {
    let rafId: number | null = null;
    if (!plateId) {
      return;
    }
    if (plateCount <= 1) {
      return;
    }
    if (
      !initialPlateZoomRef.current &&
      plateId &&
      selectedPlate?.width &&
      selectedPlate?.height &&
      canvasStageRef?.current
    ) {
      if (isScreenRoute) {
        //wait for canvas to resize after opening layers panel
        if (Math.round(experimentLoadProgress) === 100 && selectedSidePanelTab !== null) {
          const container = canvasContainerRef?.current;
          if (!container) {
            return;
          }
          let lastWidth = 0;
          let lastHeight = 0;
          let stable = 0;
          let frames = 0;
          const maxFrames = 45;
          const tick = () => {
            const { width, height } = container.getBoundingClientRect();
            if (width === lastWidth && height === lastHeight) {
              stable += 1;
            } else {
              stable = 0;
              lastWidth = width;
              lastHeight = height;
            }
            frames += 1;
            if (stable >= 2 || frames >= maxFrames) {
              zoomToPlateHandler(plateId);
              initialPlateZoomRef.current = true;
              return;
            }
            rafId = requestAnimationFrame(tick);
          };
          rafId = requestAnimationFrame(tick);
        }
      } else {
        zoomToPlateHandler(plateId);
        initialPlateZoomRef.current = true;
      }
    }
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    plateId,
    plateCount,
    selectedPlate?.width,
    selectedPlate?.height,
    isScreenRoute,
    experimentLoadProgress,
    selectedSidePanelTab,
    canvasStageRef?.current,
    canvasContainerRef?.current,
    zoomToPlateHandler,
  ]);
};

export const useFollowCanvasWell = (
  canvasStageRef: React.MutableRefObject<Konva.Stage | null>,
) => {
  const wellRect = useLastSelectedWellRect();
  const wellNavigationDetails = useAllWellNavigationDetails();

  const setWellInView = useCallback(() => {
    const stage = canvasStageRef.current;
    if (!stage || !wellRect) {
      return;
    }

    const scale = stage.scaleX();
    if (scale <= 1 || wellNavigationDetails?.length > 1) {
      return;
    }

    const stagePos = stage.position();
    const stageWidth = stage.width();
    const stageHeight = stage.height();

    const visibleX = -stagePos.x / scale;
    const visibleY = -stagePos.y / scale;
    const visibleWidth = stageWidth / scale;
    const visibleHeight = stageHeight / scale;

    const visibleMaxX = visibleX + visibleWidth;
    const visibleMaxY = visibleY + visibleHeight;

    const wellLeft = wellRect.x;
    const wellRight = wellRect.x + wellRect.w;
    const wellTop = wellRect.y;
    const wellBottom = wellRect.y + wellRect.h;
    const margin = 50 / scale;
    const isWellInView = !(
      wellRight < visibleX + margin ||
      wellLeft > visibleMaxX - margin ||
      wellBottom < visibleY + margin ||
      wellTop > visibleMaxY - margin
    );

    if (isWellInView) {
      return;
    }

    stage.position({
      x: -wellLeft * scale,
      y: -wellTop * scale,
    });

    stage.batchDraw();
  }, [wellRect, wellNavigationDetails, canvasStageRef]);

  useEffect(() => {
    setWellInView();
  }, [setWellInView]);
};

export const adjustPlatePosition = (
  stageRef: React.MutableRefObject<Konva.Stage | undefined>,
) => {
  return useCallback(
    (experiment: ExperimentDtoClient) => {
      const firstPlate = experiment.plateDto[0];
      if (firstPlate.wellRects != null && firstPlate.wellRects.length > 0) {
        const wellInfo = firstPlate.wellRects[0];
        platePosition(stageRef.current, {
          x: wellInfo.x,
          y: wellInfo.y,
          width: wellInfo.w,
          height: wellInfo.h,
        });
      }
    },
    [stageRef],
  );
};
